import type { MailPlaceholderDefinition } from "api/mailTemplates/MailTemplate.typed";
import { mailPlaceholderBodyHrefScheme, type MailPhHrefScheme } from "./mailPlaceholderContext";

export type MailPhStyle = "none" | "bold" | "italic" | "highlight";

export type TextEmphasis = "none" | "bold" | "italic" | "boldItalic";

export type MailLinePart =
  | { kind: "text"; text: string; emphasis?: TextEmphasis }
  | { kind: "link"; href: string; text: string }
  | { kind: "buttonLink"; href: string; text: string }
  | { kind: "ph"; key: string; style: MailPhStyle; hrefScheme?: MailPhHrefScheme };

export type MailParagraphContent =
  | { format: "rich"; parts: MailLinePart[] }
  | { format: "template"; html: string }
  | {
      format: "conditional";
      conditionField: string;
      ifParts: MailLinePart[];
      elseParts: MailLinePart[];
    };

export function mailPhToken(key: string): string {
  return `{{.${key}}}`;
}

export const MAIL_EDITOR_PH_CHIP_MARKER = "data-zekay-mail-ph";
export const MAIL_EDITOR_PH_KEY_ATTR = "data-zekay-mail-ph-key";
export const MAIL_EDITOR_PH_STYLE_ATTR = "data-zekay-mail-ph-style";
export const MAIL_EDITOR_PH_HREF_ATTR = "data-zekay-mail-ph-href";
export const MAIL_EDITOR_PH_CHIP_CLASS = "zekay-mail-ph-chip";

export const MAIL_EDITOR_PH_CHIP_SELECTED_CLASS = "zekay-mail-ph-chip--selected";

const PH_REGEX = /\{\{\.([A-Za-z0-9_]+)\}\}/g;

function wrapMailPhInnerHtml(inner: string, style: MailPhStyle): string {
  switch (style) {
    case "bold":
      return `<b>${inner}</b>`;
    case "italic":
      return `<i>${inner}</i>`;
    case "highlight":
      return `<span style="font-weight:600;">${inner}</span>`;
    default:
      return inner;
  }
}

function parseMailPhStyleFromEditorAttr(raw: string | null): MailPhStyle {
  if (raw === "bold" || raw === "italic" || raw === "highlight" || raw === "none") {
    return raw;
  }

  return "none";
}

function editorChipLabelInnerHtml(visibleText: string, style: MailPhStyle): string {
  return wrapMailPhInnerHtml(escapeHtmlPlainSegment(visibleText), style);
}

function parseMailPhHrefSchemeFromAttr(raw: string | null): MailPhHrefScheme | undefined {
  if (raw === "mailto" || raw === "tel") {
    return raw;
  }

  return undefined;
}

export function mailPhEditorChipHtml(
  key: string,
  style: MailPhStyle = "none",
  visibleLabel?: string,
  hrefScheme?: MailPhHrefScheme,
): string {
  const keyEsc = escapeHtmlAttr(key);
  const styleEsc = escapeHtmlAttr(style);
  const display =
    visibleLabel !== undefined && visibleLabel.trim() !== "" ? visibleLabel.trim() : key;
  const title = escapeHtmlAttr(display);
  const aria = escapeHtmlAttr(display);
  const inner = editorChipLabelInnerHtml(display, style);
  const hrefAttr =
    hrefScheme === "mailto" || hrefScheme === "tel"
      ? ` ${MAIL_EDITOR_PH_HREF_ATTR}="${hrefScheme}"`
      : "";

  return `<span ${MAIL_EDITOR_PH_CHIP_MARKER}="1" ${MAIL_EDITOR_PH_KEY_ATTR}="${keyEsc}" ${MAIL_EDITOR_PH_STYLE_ATTR}="${styleEsc}"${hrefAttr} class="${MAIL_EDITOR_PH_CHIP_CLASS}" contenteditable="false" title="${title}" aria-label="${aria}">${inner}</span>`;
}

export function parseMailPlaceholderKeyFromToken(token: string): string | null {
  const trimmed = token.trim();
  const m = trimmed.match(/^\{\{\.([A-Za-z0-9_]+)\}\}$/);

  return m ? m[1] : null;
}

export function escapeHtmlPlainSegment(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const MAIL_CTA_BUTTON_INLINE_STYLE =
  "display:inline-block;padding:10px 16px;background-color:#FFD000;border-radius:5px;box-shadow:0 5px 15px rgba(0,0,0,0.35);font-family:Arial,Helvetica,sans-serif;font-size:16px;text-decoration:none;color:#111827";

export const DEFAULT_MAIL_EDITOR_LINK_HREF = "https://example.com";

export function mailCtaButtonLinkOpenClose(href: string): { open: string; close: string } {
  const h = escapeHtmlAttr(href.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF);

  return {
    open: `<a data-zekay-mail-cta="1" href="${h}" target="_blank" rel="noopener noreferrer" style="${MAIL_CTA_BUTTON_INLINE_STYLE}"><b>`,
    close: "</b></a>",
  };
}

function anchorIsCtaButton(el: HTMLElement): boolean {
  if (el.getAttribute("data-zekay-mail-cta") === "1") {
    return true;
  }

  const st = (el.getAttribute("style") || "").toLowerCase().replace(/\s/g, "");

  return st.includes("background-color:#ffd000") || st.includes("background-color:rgb(255,208,0)");
}

function textEmphasisFromWalkCtx(ctx: { bold: boolean; italic: boolean }): TextEmphasis {
  if (ctx.bold && ctx.italic) return "boldItalic";

  if (ctx.bold) return "bold";

  if (ctx.italic) return "italic";

  return "none";
}

function textPartPayload(emphasis: TextEmphasis): { emphasis?: TextEmphasis } {
  return emphasis === "none" ? {} : { emphasis };
}

function sameTextEmphasis(a: TextEmphasis | undefined, b: TextEmphasis | undefined): boolean {
  return (a ?? "none") === (b ?? "none");
}

export function mergeAdjacentTextParts(parts: MailLinePart[]): MailLinePart[] {
  const out: MailLinePart[] = [];

  for (const p of parts) {
    if (p.kind === "text" && p.text === "") {
      continue;
    }

    const last = out[out.length - 1];

    if (p.kind === "text" && last?.kind === "text" && sameTextEmphasis(last.emphasis, p.emphasis)) {
      last.text += p.text;
    } else {
      out.push(
        p.kind === "text"
          ? { kind: "text", text: p.text, ...textPartPayload(p.emphasis ?? "none") }
          : { ...p },
      );
    }
  }

  return out;
}

function styleFromWrapperElement(el: HTMLElement, tag: string): MailPhStyle {
  const t = tag.toLowerCase();

  if (t === "b" || t === "strong") {
    return "bold";
  }

  if (t === "i" || t === "em") {
    return "italic";
  }

  if (t === "span") {
    const st = (el.getAttribute("style") || "").toLowerCase();

    if (
      st.includes("font-weight:600") ||
      st.includes("font-weight: 600") ||
      st.includes("font-weight:bold")
    ) {
      return "highlight";
    }
  }

  return "none";
}

function splitTextNodeIntoParts(
  text: string,
  ctx: { bold: boolean; italic: boolean },
): MailLinePart[] {
  const parts: MailLinePart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(PH_REGEX.source, "g");
  const em = textEmphasisFromWalkCtx(ctx);

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      parts.push({ kind: "text", text: text.slice(last, m.index), ...textPartPayload(em) });
    }

    parts.push({ kind: "ph", key: m[1], style: "none" });
    last = re.lastIndex;
  }

  if (last < text.length) {
    parts.push({ kind: "text", text: text.slice(last), ...textPartPayload(em) });
  }

  return mergeAdjacentTextParts(parts);
}

function stripLegacyEmptyWrapLabelPrefix(label: string): string {
  return label.replace(/^texte(?=\S)/u, "").trimStart();
}

function parseContactPlaceholderAnchor(
  el: HTMLElement,
  href: string,
): Extract<MailLinePart, { kind: "ph" }> | null {
  const matched = href.trim().match(/^(mailto|tel):\{\{\.([A-Za-z0-9_]+)\}\}$/);

  if (!matched) {
    return null;
  }

  const scheme = parseMailPhHrefSchemeFromAttr(matched[1]);

  if (!scheme) {
    return null;
  }
  const key = matched[2];
  const token = mailPhToken(key);
  const chip = el.querySelector(`[${MAIL_EDITOR_PH_CHIP_MARKER}="1"]`);

  if (chip instanceof HTMLElement && chip.getAttribute(MAIL_EDITOR_PH_KEY_ATTR)?.trim() === key) {
    return {
      kind: "ph",
      key,
      style: parseMailPhStyleFromEditorAttr(chip.getAttribute(MAIL_EDITOR_PH_STYLE_ATTR)),
      hrefScheme: scheme,
    };
  }

  if ((el.textContent ?? "").trim() !== token) {
    return null;
  }

  const child = el.firstElementChild;
  const tag = child?.tagName.toLowerCase() ?? "";
  const style =
    child && ["b", "strong", "i", "em", "span"].includes(tag)
      ? styleFromWrapperElement(child as HTMLElement, tag)
      : "none";

  return { kind: "ph", key, style, hrefScheme: scheme };
}

function walkChildNodesToParts(
  nodes: ChildNode[],
  ctx: { bold: boolean; italic: boolean },
): MailLinePart[] {
  const raw: MailLinePart[] = [];

  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent ?? "";

      if (t) {
        raw.push(...splitTextNodeIntoParts(t, ctx));
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === "span" && el.getAttribute(MAIL_EDITOR_PH_CHIP_MARKER) === "1") {
        const keyRaw = el.getAttribute(MAIL_EDITOR_PH_KEY_ATTR)?.trim() ?? "";

        if (/^[A-Za-z0-9_]+$/.test(keyRaw)) {
          raw.push({
            kind: "ph",
            key: keyRaw,
            style: parseMailPhStyleFromEditorAttr(el.getAttribute(MAIL_EDITOR_PH_STYLE_ATTR)),
            hrefScheme: parseMailPhHrefSchemeFromAttr(el.getAttribute(MAIL_EDITOR_PH_HREF_ATTR)),
          });
          continue;
        }
      }

      const inner = el.innerHTML.trim();
      const directPh = inner.match(/^\{\{\.([A-Za-z0-9_]+)\}\}$/);

      if (directPh && ["b", "strong", "i", "em", "span"].includes(tag)) {
        raw.push({ kind: "ph", key: directPh[1], style: styleFromWrapperElement(el, tag) });
      } else if (tag === "a") {
        const href = el.getAttribute("href") || "";
        const contactPh = parseContactPlaceholderAnchor(el, href);

        if (contactPh) {
          raw.push(contactPh);
        } else {
          const label = stripLegacyEmptyWrapLabelPrefix((el.textContent ?? "").trim());

          if (href && anchorIsCtaButton(el)) {
            raw.push({ kind: "buttonLink", href, text: label });
          } else if (href) {
            raw.push({ kind: "link", href, text: label });
          }
        }
      } else if (tag === "br") {
        raw.push({ kind: "text", text: "\n", ...textPartPayload(textEmphasisFromWalkCtx(ctx)) });
      } else if (tag === "b" || tag === "strong") {
        raw.push(...walkChildNodesToParts(Array.from(el.childNodes), { ...ctx, bold: true }));
      } else if (tag === "i" || tag === "em") {
        raw.push(...walkChildNodesToParts(Array.from(el.childNodes), { ...ctx, italic: true }));
      } else {
        raw.push(...walkChildNodesToParts(Array.from(el.childNodes), ctx));
      }
    }
  }

  return mergeAdjacentTextParts(raw);
}

export function parseHtmlToMailLineParts(html: string): MailLinePart[] {
  const trimmed = html.trim();

  if (!trimmed) {
    return [{ kind: "text", text: "" }];
  }

  try {
    const doc = new DOMParser().parseFromString(`<div id="root">${trimmed}</div>`, "text/html");
    const root = doc.getElementById("root");

    if (!root) {
      return parsePlainStringToMailLineParts(trimmed.replace(/<[^>]+>/g, ""));
    }

    const parts = walkChildNodesToParts(Array.from(root.childNodes), {
      bold: false,
      italic: false,
    });

    return parts.length > 0 ? parts : [{ kind: "text", text: "" }];
  } catch {
    return parsePlainStringToMailLineParts(trimmed.replace(/<[^>]+>/g, ""));
  }
}

const MAIL_CONDITION_FIELD_GO_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function sanitizeMailConditionFieldStorageKey(raw: string): string {
  const key = raw.replace(/[^A-Za-z0-9_]/g, "");

  return key.length > 0 ? key : "Condition";
}

export function mailConditionFieldKeyToDisplayParts(key: string): MailLinePart[] {
  const t = key.trim();

  if (!t) {
    return [{ kind: "text", text: "" }];
  }

  if (MAIL_CONDITION_FIELD_GO_IDENT_RE.test(t)) {
    return [{ kind: "ph", key: t, style: "none" }];
  }

  return [{ kind: "text", text: t }];
}

export function mailLinePartsToConditionFieldKey(parts: MailLinePart[]): string {
  const merged = mergeAdjacentTextParts(parts);
  const chunks: string[] = [];

  for (const p of merged) {
    if (p.kind === "text") {
      chunks.push(p.text);
    } else if (p.kind === "ph") {
      chunks.push(p.key);
    } else if (p.kind === "link" || p.kind === "buttonLink") {
      chunks.push(p.text);
    }
  }

  return chunks.join("").trim();
}

export function parsePlainStringToMailLineParts(plain: string): MailLinePart[] {
  const parts: MailLinePart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(PH_REGEX.source, "g");

  while ((m = re.exec(plain)) !== null) {
    if (m.index > last) {
      parts.push({ kind: "text", text: plain.slice(last, m.index) });
    }

    parts.push({ kind: "ph", key: m[1], style: "none" });
    last = re.lastIndex;
  }

  if (last < plain.length) {
    parts.push({ kind: "text", text: plain.slice(last) });
  }

  return mergeAdjacentTextParts(parts.length > 0 ? parts : [{ kind: "text", text: "" }]);
}

export function mailLinePartsForSubjectField(parts: MailLinePart[]): MailLinePart[] {
  const mapped: MailLinePart[] = [];

  for (const p of parts) {
    if (p.kind === "ph") {
      mapped.push({ kind: "ph", key: p.key, style: "none" });
      continue;
    }

    const text = p.text.replace(/[\n\r]+/g, " ");

    mapped.push({ kind: "text", text });
  }

  return mergeAdjacentTextParts(mapped.length > 0 ? mapped : [{ kind: "text", text: "" }]);
}

export function serializeMailLinePartsToSubject(parts: MailLinePart[]): string {
  return linePartsToPlainPreview(mailLinePartsForSubjectField(parts)).replace(/[\n\r]+/g, " ");
}

function wrapTextEmphasisInner(inner: string, emphasis: TextEmphasis | undefined): string {
  const em = emphasis ?? "none";

  if (em === "boldItalic") return `<b><i>${inner}</i></b>`;

  if (em === "bold") return `<b>${inner}</b>`;

  if (em === "italic") return `<i>${inner}</i>`;

  return inner;
}

function serializeTextPartToInnerHtmlWithBreaks(
  text: string,
  emphasis: TextEmphasis | undefined,
): string {
  const segments = text.split("\n");

  return segments
    .map((seg) => wrapTextEmphasisInner(escapeHtmlPlainSegment(seg), emphasis))
    .join("<br>");
}

function serializeNonPhLinePartToHtml(p: MailLinePart): string | null {
  if (p.kind === "text") {
    return serializeTextPartToInnerHtmlWithBreaks(p.text, p.emphasis);
  }

  if (p.kind === "link") {
    const h = escapeHtmlAttr(p.href);
    const t = escapeHtmlPlainSegment(p.text);

    return `<a href="${h}" target="_blank" rel="noopener noreferrer">${t}</a>`;
  }

  if (p.kind === "buttonLink") {
    const { open, close } = mailCtaButtonLinkOpenClose(p.href);
    const t = escapeHtmlPlainSegment(p.text);

    return `${open}${t}${close}`;
  }

  return null;
}

export function serializeMailLinePartsToInnerHtml(
  parts: MailLinePart[],
  catalog?: readonly MailPlaceholderDefinition[],
): string {
  return parts
    .map((p) => {
      const shared = serializeNonPhLinePartToHtml(p);

      if (shared !== null) {
        return shared;
      }

      if (p.kind !== "ph") {
        return "";
      }

      const token = mailPhToken(p.key);
      const inner = wrapMailPhInnerHtml(token, p.style);
      const scheme = p.hrefScheme ?? mailPlaceholderBodyHrefScheme(p.key, catalog);

      if (scheme) {
        return `<a href="${escapeHtmlAttr(`${scheme}:${token}`)}">${inner}</a>`;
      }

      return inner;
    })
    .join("");
}

export type SerializeMailLinePartsToEditorOptions = {
  presentablePhLabel?: (key: string) => string;
};

export function serializeMailLinePartsToEditorInnerHtml(
  parts: MailLinePart[],
  options?: SerializeMailLinePartsToEditorOptions,
): string {
  return parts
    .map((p) => {
      const shared = serializeNonPhLinePartToHtml(p);

      if (shared !== null) {
        return shared;
      }

      if (p.kind !== "ph") {
        return "";
      }

      const fromCatalog = options?.presentablePhLabel?.(p.key);
      const visible =
        fromCatalog !== undefined && fromCatalog.trim() !== "" ? fromCatalog.trim() : undefined;

      return mailPhEditorChipHtml(p.key, p.style, visible, p.hrefScheme);
    })
    .join("");
}

export function linePartsToPlainPreview(parts: MailLinePart[]): string {
  return parts
    .map((p) => {
      if (p.kind === "text") return p.text;

      if (p.kind === "link") return p.text || p.href;

      if (p.kind === "buttonLink") return p.text || p.href;

      return mailPhToken(p.key);
    })
    .join("");
}

export function appendPlaceholderToParts(parts: MailLinePart[], key: string): MailLinePart[] {
  const next = [...parts, { kind: "ph" as const, key, style: "none" as MailPhStyle }];

  return mergeAdjacentTextParts(next);
}

export function insertPlaceholderAfterPartIndex(
  parts: MailLinePart[],
  key: string,
  afterPartIndex: number | null,
): MailLinePart[] {
  const ph: MailLinePart = { kind: "ph", key, style: "none" };
  if (afterPartIndex === null) {
    return mergeAdjacentTextParts([...parts, ph]);
  }

  const insertAt = Math.min(afterPartIndex + 1, parts.length);
  const next = [...parts.slice(0, insertAt), ph, ...parts.slice(insertAt)];

  return mergeAdjacentTextParts(next);
}

export function paragraphRichDefault(): MailParagraphContent {
  return { format: "rich", parts: [{ kind: "text", text: "" }] };
}

export function paragraphTemplateDefault(template: string): MailParagraphContent {
  return { format: "template", html: template };
}

export function paragraphConditionalDefault(): Extract<
  MailParagraphContent,
  { format: "conditional" }
> {
  return {
    format: "conditional",
    conditionField: "Condition",
    ifParts: [{ kind: "text", text: "" }],
    elseParts: [{ kind: "text", text: "" }],
  };
}

const CONDITIONAL_IF_OPEN_RE = /^\{\{\s*if\s+\.([A-Za-z0-9_]+)\s*\}\}/;

export function trimmedHtmlStartsWithGoIfDirective(html: string): boolean {
  return CONDITIONAL_IF_OPEN_RE.test(html.trim());
}
const CONDITIONAL_ELSE_RE = /\{\{\s*else\s*\}\}/;
const CONDITIONAL_END_RE = /\{\{\s*end\s*\}\}/g;

export function tryParseConditionalTemplateHtml(html: string): {
  conditionField: string;
  ifParts: MailLinePart[];
  elseParts: MailLinePart[];
} | null {
  const trimmed = html.trim();
  const openMatch = trimmed.match(CONDITIONAL_IF_OPEN_RE);

  if (!openMatch) {
    return null;
  }

  const conditionField = openMatch[1];
  let body = trimmed.slice(openMatch[0].length);
  let lastEnd = -1;
  let endMatch: RegExpExecArray | null;

  CONDITIONAL_END_RE.lastIndex = 0;

  while ((endMatch = CONDITIONAL_END_RE.exec(body)) !== null) {
    lastEnd = endMatch.index;
  }

  if (lastEnd < 0) {
    return null;
  }

  body = body.slice(0, lastEnd).trim();
  CONDITIONAL_ELSE_RE.lastIndex = 0;
  const elseExec = CONDITIONAL_ELSE_RE.exec(body);
  let ifHtml: string;
  let elseHtml: string;

  if (!elseExec) {
    ifHtml = body;
    elseHtml = "";
  } else {
    ifHtml = body.slice(0, elseExec.index).trim();
    elseHtml = body.slice(elseExec.index + elseExec[0].length).trim();
  }

  return {
    conditionField,
    ifParts: mergeAdjacentTextParts(parseHtmlToMailLineParts(ifHtml)),
    elseParts: mergeAdjacentTextParts(parseHtmlToMailLineParts(elseHtml)),
  };
}

export function serializeConditionalParagraphToInnerTemplate(
  conditionField: string,
  ifParts: MailLinePart[],
  elseParts: MailLinePart[],
  catalog?: readonly MailPlaceholderDefinition[],
): string {
  const key = conditionField.trim().replace(/[^A-Za-z0-9_]/g, "") || "Condition";
  const ifInner = serializeMailLinePartsToInnerHtml(ifParts, catalog).trim();
  const elseInner = serializeMailLinePartsToInnerHtml(elseParts, catalog).trim();

  return `{{if .${key}}}\n${ifInner}\n{{else}}\n${elseInner}\n{{end}}`;
}
